<template>
  <div class="invoice-send-wrap">
    <portal to="dash-modal">
      <MollieNoProfileModal 
        v-if="showMollieModal && showMollieModal === 'no-profile'"
        @close="showMollieModal = false"
      />
      <MollieMissingDataModal 
        v-if="showMollieModal && showMollieModal === 'missing-data'"
        @close="showMollieModal = false"
      />
      <RootlineErrorModal 
        v-if="showRootlineModal && rootlineStatus !== 'verified'"
        :status="rootlineStatus"
        @close="onCancelRootlineModal"
      />
      
      <InvoiceSendAdjustModal 
        v-if="showAdjustModal"
        :invoice-id="sendDetails.invoiceId"
        :mark-as-sent="markedAsSent"
        @send-invoice="sendInvoice"
        @close="showAdjustModal = false"
        @marked-as-sent="() => { $emit('invoice-updated'); showAdjustModal = false }"
      />
    </portal>
    <div class="invoice-wrap-bg">
      <div class="invoice-wrap">
        <LoadingSpinner v-if="loading || isSending" :display-text="(isSending) ? ((isQuotation) ? $gettext('Sharing quotation') : $gettext('Sending invoice')): ''" />
        <b-form v-if="!loading" @submit.prevent="adjustAndSend" @keydown.enter="adjustAndSend" class="invoice-send-form gk-input-light--delete">

          <div v-if="showRecurrentWarningTxt">
            <b-alert show class="gk-alert mb-0" variant="warning">
              <div>
                {{ $gettext('Your planning has been saved but if you want to send out the invoice today you need manually send out this first invoice.') }}
              </div>
            </b-alert>
          </div>

          <b-form-group 
            id="input-group-recipients"
            :label="$gettext('Recipients*')"
            label-for="input-recipients"
            label-class="gk-label-margin gk-modal-label"
          >
            <b-input-group>
              <EmailFormInput v-if="customerLoaded" :preselected="sendDetails.recipients" id="input-recipients" ref="email_form_input" />
            </b-input-group>

            <small tabindex="-1" id="input-group-recipients__BV_description_" class="form-text text-muted" style="font-size: 14px;">{{ $gettext('You can add multiple email addresses') }}</small>

            <b-form-checkbox
              id="checkbox-includeme"
              v-model="updateCC"
              @change="updateCCSave"
              name="checkbox-includeme"
            >
              <span v-if="isQuotation">{{ $gettext('Send me a copy of this Quotation') }}</span>
              <span v-else>{{ $gettext('Send me a copy of this Invoice') }}</span>
            </b-form-checkbox>

          </b-form-group>

          <b-form-group
            id="input-group-subject"
            :label="$gettext('Subject*')"
            label-for="input-subject"
            label-class="gk-label-margin gk-modal-label"
          >
            <b-form-input v-if="currentCompany.is_premium" id="input-subject" v-model="sendDetails.subject" />
            <b-form-input v-else id="input-subject" v-model="sendDetails.subject" readonly @click="$store.commit('togglePremiumModal', 'web')" />
          </b-form-group>

          <b-form-group id="input-group-message" label-class="gk-label-margin gk-modal-label" label-for="input-message">
            <template slot="label">
              <span v-translate>Message</span>
            </template>
            <b-input-group class="mb-2">
              <div class="premium-textarea-wrapper">
                <b-form-textarea id="input-message" v-model="sendDetails.message" required :placeholder="$gettext('Dear...')" rows="6" no-resize />
                
                <div class="premium-info text-blue" v-if="sendDetails.attach_mollie_payment">
                  <p>{{ $gettext('Pay this invoice directly using a payment link') }} <font-awesome-icon class="mx-2" role="button" @click="detachMolliePayment" icon="fa-solid fa-xmark" /></p>
                </div>

                <div class="premium-info text-blue" v-if="sendDetails.attach_rootline_payment">
                  <p>{{ $gettext('Pay this invoice directly using a payment link') }} <font-awesome-icon class="mx-2" role="button" @click="detachRootlinePayment" icon="fa-solid fa-xmark" /></p>
                </div>
                
                <div v-if="!currentCompany.is_premium" class="premium-info text-unassigned" id="non-premium-text" v-b-tooltip.hover.top="$gettext('You can remove this text by upgrading to Gekko Premium')">
                  <p>
                    <span>{{ emailMessage }}</span>
                  </p>
                  <p v-translate>Gekko: the friendly and free online bookkeeping tool for small business owners, <span>http://getgekko.com</span></p>
                </div>
              </div>
            </b-input-group>

            <b-form-checkbox
              id="checkbox-paymentlink"
              v-if="!isQuotation && !sendDetails.attach_mollie_payment && mollieStatus === 'ok'"
              v-model="sendDetails.attach_mollie_payment"
              @input="attachMolliePayment"
              name="checkbox-paymentlink"
              :disabled="mollieDisabled"
            >
              {{ $gettext('Add online payment link') }}
              <font-awesome-icon id="tooltip-paymentlink" class="tooltip-icon tooltip-icon--question ml-1" icon="fa-regular fa-circle-question" />
              <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-paymentlink" triggers="hover" container="#report-container">
                <div class="tooltip-text" v-html="$gettext('<span style=\'color: white; font-weight: bold; text-decoration: underline;\'>Pay this invoice directly using a payment link.</span> This will add a payment link to your email that allows your customer to pay this invoice directly via online payment.')" />
              </b-tooltip>
            </b-form-checkbox>

            <b-form-checkbox
              id="checkbox-rootline-paymentlink"
              v-if="!isQuotation && !sendDetails.attach_rootline_payment && mollieStatus !== 'ok' && currentCompany.country === 'NL'"
              v-model="sendDetails.attach_rootline_payment"
              @input="attachRootlinePayment"
              name="checkbox-rootline-paymentlink"
              :disabled="rootlineDisabled"
            >
              {{ $gettext('Add online payment link') }}
              <font-awesome-icon id="tooltip-rootline-paymentlink" class="tooltip-icon tooltip-icon--question ml-1" icon="fa-regular fa-circle-question" />
              <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-rootline-paymentlink" triggers="hover" container="#report-container">
                <div class="tooltip-text" v-html="$gettext('<span style=\'color: white; font-weight: bold; text-decoration: underline;\'>Pay this invoice directly using a payment link.</span> This will add a payment link to your email that allows your customer to pay this invoice directly via online payment.')" />
              </b-tooltip>
            </b-form-checkbox>

            <div v-if="sendDetails.attach_rootline_payment && !isPremium" class="gk-tip gk-tip--warning gk-tip--premium mb-3" v-translate>
              Adding a payment link will on average make your customer pay your invoice 3 times faster. We will charge you 80 cents per transaction. If you think this is too much, please consider upgrading to Premium and you will only pay 25 cents per transaction.
            </div>
            
            <div v-if="currencyNotEuro" class="text-info" v-translate>You cannot send payment links for invoices that are not in Euro.</div>

            <div v-if="amountLTEZero && rootlineStatus !== 'verified'" class="text-info" v-translate>You cannot send payment links for invoices with negative totals.</div>
            <div v-if="amountLTEZero && rootlineStatus === 'verified'" class="text-info" v-translate>You cannot send payment links for invoices with totals smaller than 1 euro.</div>
            
            <div v-if="showMollieError" class="text-danger">{{ mollieAttachError }}</div>
            <div v-if="showRootlineError" class="text-danger">{{ rootlineAttachError }}</div>

          </b-form-group>

          <b-form-group id="input-group-3" label-class="gk-label-margin gk-modal-label" label-for="input-3">
            <template slot="label">
              <span v-translate>Attachments</span>
            </template>

            <div class="attachments-table-wrap">
              <b-table 
                :items="fullAttachmentsList"
                :fields="documentFields"
                class="attachments-table"
                :class="['gk-table gk-table--hover gk-table--responsive gk-table--headed']"
                responsive
              >
                <template #cell(title)="row">
                  <span v-if="row.item.key === 'mainpdf'">
                    {{ row.item.title }}
                    <a class="mx-2 text-blue" v-if="!isQuotation" :href="Urls['invoice_download_pdf'](invoice.id)" title="Download attached invoice">
                      <font-awesome-icon icon="fa-solid fa-download" />
                    </a>
                    <a class="mx-2 text-blue" v-else :href="Urls['quotation_download_pdf'](invoice.id)" title="Download attached quotation">
                      <font-awesome-icon icon="fa-solid fa-download" />
                    </a>
                  </span>
                  <span v-else-if="row.item.key === 'hoursreport'">
                    {{ row.item.title }}
                    <a class="mx-2 text-blue" :href="downloadHoursReport()" title="Download attached pdf hours report">
                      <font-awesome-icon icon="fa-solid fa-download" />
                    </a>
                  </span>
                  <span v-else>
                    {{ row.item.title }}
                  </span>
                </template>

                <template #cell(total)="row">
                  <span v-if="row.item.file_size">
                    {{ niceBytes(row.item.file_size, 1000, true) }}
                  </span>
                  <span v-else-if="row.item.key === 'mainpdf' || row.item.key === 'costsreport'">
                    {{ invoice.currency }} {{ row.item.total | formatNumber }}
                  </span>
                  <!-- <span v-else-if="row.item.key === 'hoursreport'">
                    {{ row.item.total }} {{ $gettext('hours') }}
                  </span> -->
                </template>
                
                <template #cell(actions)="row">
                  <GekkoButton 
                    :disabled="row.item.key === 'mainpdf'"
                    @click="removeDocumentFromList(row)"
                    icon="fa-solid fa-trash"
                    variant="link"
                    class="text-danger"
                  />
                </template>
              </b-table>
            </div>

            <b-form-checkbox
              id="checkbox-ubl"
              v-if="!isQuotation"
              v-show="!sendDetails.attach_ubl"
              v-model="sendDetails.attach_ubl"
              name="checkbox-ubl"
            >
              {{ $gettext('Send UBL with invoice') }}
              <font-awesome-icon id="tooltip-ubl" class="tooltip-icon tooltip-icon--question ml-1" icon="fa-regular fa-circle-question" />
              <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-ubl" triggers="hover" container="#report-container">
                <div class="tooltip-text">
                  {{ $gettext('UBL (Universal Business Language) is a standard for invoices that is used across the world. It is a format used in many accountancy applications.') }}
                </div>
              </b-tooltip>
            </b-form-checkbox>

            <b-form-checkbox
              id="checkbox-attach_costs"
              v-if="attachedCosts && attachedCosts.count > 0"
              v-show="!sendDetails.attach_costs"
              v-model="sendDetails.attach_costs"
              name="checkbox-attach_costs"
            >
              {{ $gettext('Attach the cost proofs of your imported cost[s]') }}
            </b-form-checkbox>

            <b-form-checkbox
              id="checkbox-attach_hours"
              v-if="attachedHours && attachedHours > 0"
              v-show="!sendDetails.attach_hours"
              v-model="sendDetails.attach_hours"
              name="checkbox-attach_hours"
            >
              {{ $gettext('Attach report of imported hours (.pdf)') }}
            </b-form-checkbox>

            <div>
              <div v-show="showDocumentSelector">
                <DocumentSelector 
                  ref="document_selector"
                  show-file-uploader
                  @documentSelected="documentSelected"
                  @showFileUploader="displayFileUploader"
                />
              </div>
              <GekkoButton 
                v-if="!showDocumentSelector"
                @click="displayDocumentSelector"
                :text="$gettext('Add another file')"
                icon="fa-solid fa-plus"
                variant="link"
                class="px-0"
              />
            </div>
          </b-form-group>

          <FileUploaderFull 
            v-if="showFileUploader"
            :document-name="$gettext('invoice') + ' ' + invoice.number"
            @document-selected="documentSelected"
          />

        </b-form>
      </div>
    </div>
  </div>

</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { niceBytes } from "@/utils/";

import invoices from "@/mixins/invoices";

import InvoiceRecurrentService from '@/store/services/invoice.recurrent.service.js';
import InvoiceService from '@/store/services/invoice.service.js';
import QuotationService from "@/store/services/quotation.service.js"

import IdealPaymentService from '@/store/services/idealpayment.service.js';
import CompanySettingService from '@/store/services/companysetting.service.js';
import EmailMessageNotificationService from "@/store/services/email-message-notification.service.js";
import CostService from "@/store/services/cost.service.js";
import HourService from "@/store/services/hour.service.js";

import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import EmailFormInput from "@/components/ui/EmailFormInput";
import FileUploaderFull from "@/components/documents/FileUploaderFull";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import DocumentSelector from "@/components/documents/DocumentSelector";
import MollieNoProfileModal from "@/components/invoices/ui/MollieNoProfileModal"
import MollieMissingDataModal from "@/components/invoices/ui/MollieMissingDataModal"
import RootlineErrorModal from "@/components/invoices/ui/RootlineErrorModal"
import InvoiceSendAdjustModal from "@/components/invoices/ui/InvoiceSendAdjustModal"

export default {
  name: "InvoiceSend",
  mixins: [invoices],
  components: {
    TitleBar,
    GekkoButton,
    EmailFormInput,
    FileUploaderFull,
    LoadingSpinner,
    DocumentSelector,
    MollieNoProfileModal,
    MollieMissingDataModal,
    RootlineErrorModal,
    InvoiceSendAdjustModal
  },
  props: {
    isQuotation: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      recurrentResponse: false,
      sendDetails: {
        recipients: '',
        subject: '',
        message: '',
        attach_costs: false,
        attach_hours: false,
        attach_ubl: false,
        attach_mollie_payment: false,
        attach_rootline_payment: false
      },
      loading: true,
      customerLoaded: false,
      updateCC: false,
      invoiceNumber: null,
      isSending: false,
      today: null,
      showUpdate: null,
      mollieStatus: null,
      rootlineStatus: null,
      mollieAttachError: null,
      rootlineAttachError: null,
      mollieDisabled: false,
      rootlineDisabled: false,
      showMollieError: false,
      showRootlineError: false,
      showMollieModal: false,
      showRootlineModal: false,
      showAdjustModal: false,
      amountLTEZero: false,
      currencyNotEuro: false,
      attachedCosts: null,
      attachedHours: null,
      showDocumentSelector: false,
      showFileUploader: false,
      mainInvoicePDF: {
        key: 'mainpdf',
        title: '',
        total: '',
        description: ''
      },
      invoiceUBL: {
        key: 'ublfile',
        title: this.$gettext('UBL File attached'),
        description: ''
      },
      attachedHoursReport: {
        key: 'hoursreport',
        title: this.$gettext('Report of attached hours (.pdf)'),
        total: '',
        description: ''
      },
      selectedDocuments: [],
      documentFields: [
        { key: "title", label: this.$gettext('Title'), class: 'tablecol-md' },
        { key: "total", label: this.$gettext('Total / Size'), class: 'tablecol-sm' },
        { key: "description", label: this.$gettext('Description'), class: 'tablecol-md' },
        { key: "actions", label: "", tdClass: "text-right" }
      ],
      markedAsSent: false
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      isPremium: 'getIsPremium',
      invoice: "invoices/getInvoice",
      storeSendDetails: 'invoices/getSendDetails',
      quotationFiles: 'invoices/getQuotationFiles',
      brandById: "brands/getBrandById",
      scheduledForToday: "invoices/getScheduledForToday"
    }),
    showRecurrentWarningTxt () {
      return this.scheduledForToday
    },
    emailMessage(){
      var message = this.$gettext('This email has been sent to you via Gekko on behalf of %{ company_name }.');
      message = message.replace("%{ company_name }", this.currentCompany.name);
      return message;
    },
    sendText () {
      let text = this.$gettext('Send invoice')
      return text
    },
    recipientsFullString () {
      return (this.$refs.email_form_input) ? this.$refs.email_form_input.getFullString() : ''
    },
    fullAttachmentsList () {
      const fullList = [this.mainInvoicePDF].concat(this.selectedDocuments)

      if (this.sendDetails.attach_costs) {
        this.attachedCosts.results.forEach(cost => {
          const costReport = {
            key: 'costsreport',
            title: cost.number,
            total: cost.amount,
            description: cost.description
          }
          fullList.push(costReport)
        })
      }

      if (this.sendDetails.attach_hours) {
        fullList.push(this.attachedHoursReport)
      }

      if (this.sendDetails.attach_ubl) {
        fullList.push(this.invoiceUBL)
      }
      return fullList
    },
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  beforeDestroy () {
    this.sendDetails.recipients = this.recipientsFullString
    this.setSendDetails(this.sendDetails)
  },
  methods: {
    ...mapMutations ({ 
      setSendDetails: "invoices/SET_INVOICE_SEND_DETAILS",
      setCompanySettings: "main/SET_CURRENT_COMPANY_SETTINGS",
      setInvoiceStatus: 'invoices/SET_INVOICE_STATUS'
    }),
    ...mapActions ({ 
      getNextInvoiceNumber: "invoices/getNextInvoiceNumber"
    }),
    close () {
      this.$emit('close')
      document.body.classList.remove('modal-on')
    },
    setupData () {
      if (this.isQuotation) {
        this.updateCC = this.currentCompany.companysettings.include_sender_in_cc_quotation
      } else {
        this.updateCC = this.currentCompany.companysettings.include_sender_in_cc
      }
      this.today = new Date().toString('yyyy-MM-dd')
      let invoiceId
      if (this.isQuotation) {
        invoiceId = this.$route.params['quotationId']
      } else {
        invoiceId = this.$route.params['invoiceId']
      }
      if (invoiceId && Number.isInteger(parseFloat(invoiceId))) {
        // get invoice data and set default email values
        // get recurrent information if any
        InvoiceRecurrentService.getRecurrentInformation(this.invoice.id).then((response) => {
          let invoice_id
          if (response.data.recurrent) {
            this.recurrentResponse = response.data
            invoice_id = response.data.number
          } else {
            invoice_id = invoiceId
          }
          this.getInvoiceData(invoice_id)
        })
        
      } else {
        this.$router.push({ name: 'Invoices' })
      }
    },
    getInvoiceData (invoice_id) {
      this.$set(this.sendDetails, 'invoiceId', invoice_id)

      const isDefaultNumber = /^\d{4}-\d{6}$/.test(this.invoice.number)
      const backupNumber = this.invoice.number
      const currentYear = { 'year': new Date(this.today).getFullYear() }
      const data = { 'sender_company': this.currentCompany.id, 'year': currentYear }
      
      this.getNextInvoiceNumber(data).then((response) => {
        const newInvoiceNumber = response.data
        if (isDefaultNumber && currentYear != new Date(this.invoice.invoice_date).getFullYear()) {
          this.invoiceNumber = newInvoiceNumber
          this.showUpdate = true
        } else {
          this.invoiceNumber = backupNumber
          this.showUpdate = false
        }
        // this.loading = false
        
        if (this.recurrentResponse && this.recurrentResponse.recurrent) {
          this.$emit('display-buttons', 'send-recurrent')
        } else {
          this.$emit('display-buttons', 'send')
        }

      })
      this.$set(this.sendDetails, 'attach_mollie_payment', this.currentCompany.attach_mollie_payment)


      const invoice_amount = Math.max(parseFloat(this.invoice.total_amount), parseFloat(this.invoice.currency_total_amount))
      this.amountLTEZero = invoice_amount <= 0;
      if (!this.isQuotation) {
        this.setMollieDetails()
        this.setRootlineDetails()
      }

      if (this.isQuotation) {
        this.mainInvoicePDF.title = this.$gettext('Quotation #') + ' ' + this.invoice.number
        this.mainInvoicePDF.description = this.$gettext('valid until') + ' ' + this.invoice.due_date
      } else {
        this.mainInvoicePDF.title = this.$gettext('Invoice #') + ' ' + this.invoice.number
        if (!this.invoice.paid_up_front) {
          this.mainInvoicePDF.description = this.$gettext('due on') + ' ' + this.invoice.due_date
        }
      }

      if (!this.invoice.due_date) this.mainInvoicePDF.description = ''

      this.mainInvoicePDF.total = invoice_amount

      this.$set(this.sendDetails, 'attach_ubl', this.currentCompany.companysettings.attach_ubl_to_invoice)
      if (!this.isQuotation) {
        this.$set(this.sendDetails, 'message', this.currentCompany.companysettings.default_invoice_email_message)
      }

      // if this is a recurrent invoice scheduledForToday 
      if (this.scheduledForToday)  {
        // need to refresh invoice list
        this.$store.commit('invoices/REFRESH_INVOICE_LIST')
      }

      let sent_from = this.currentCompany.name
      if (this.invoice.brand) {
        const invoice_brand = this.brandById(this.invoice.brand)
        if (invoice_brand && invoice_brand.name) {
          sent_from = invoice_brand.name
        }
      }
      this.$set(this.sendDetails, 'subject', this.invoice.number + ' ' + this.$gettext('from') + ' ' + sent_from)

      if (
        (this.isQuotation && this.invoice.status !== 'draft') || 
        (!this.isQuotation && this.invoice.status !== 'd')
      ) {
        this.$set(this.sendDetails, 'subject', this.$gettext('Reminder') + ' ' + this.invoice.number + ' ' + this.$gettext('from') + ' ' + sent_from)
      }

      this.setCustomerData(this.invoice.recipient_customer)

      if (this.isQuotation) {
        this.setQuotationDefaultFiles()
      }

      CostService.list({ invoice: invoice_id }).then((response) => {
        this.attachedCosts = response.data

        HourService.count({ invoice: invoice_id }).then( (response) => {
          this.attachedHours = response.data
          this.attachedHoursReport.total = this.attachedHours

          this.loading = false

        }).catch((error) => {
          console.log('could not retrieve hours', error)
          this.loading = false
        })

      }).catch((error) => {
        console.log('could not retrieve costs', error)
        this.loading = false
      })
      
    },
    setQuotationDefaultFiles () {
      this.selectedDocuments = this.selectedDocuments.concat(this.quotationFiles)
    },
    setMollieDetails () {
      this.currencyNotEuro = this.invoice.currency != 'EUR';

      if (this.currencyNotEuro || this.amountLTEZero) {
        this.$set(this.sendDetails, 'attach_mollie_payment', false)
        this.mollieDisabled = true
      } else {
        IdealPaymentService.getConnectionStatus().then((response) => {
          if (response.status == 200) {
            this.mollieStatus = 'ok'
          } else {
            switch (response.status) {
              case 298:
                this.mollieStatus = 'no-profile'
                this.mollieAttachError = this.$gettext('Your Mollie profile could not be found.')
                break;
              case 299:
                this.mollieStatus = 'missing-data'
                this.mollieAttachError = this.$gettext('Your Mollie profile is missing data.')
                break;
            }
            this.$set(this.sendDetails, 'attach_mollie_payment', false)
            this.updateMollieOnCompanySettings()
          }
        }).catch((error) => {
          if(error.status == 499){
            this.mollieStatus = 'not-premium';
            this.$set(this.sendDetails, 'attach_mollie_payment', false)
            this.updateMollieOnCompanySettings()
          } else {
            this.mollieStatus = 'mollie-error';
            this.mollieAttachError = this.$gettext('Your Mollie connection is not working right now.')
          }
        })
      }
    },
    setRootlineDetails() {
      this.currencyNotEuro = this.invoice.currency != 'EUR';

      if (this.currencyNotEuro || this.amountLTEZero) {
        this.$set(this.sendDetails, 'attach_rootline_payment', false)
        this.rootlineDisabled = true
      } else {
        IdealPaymentService.getRootlineConnectionStatus().then((response) => {
          this.rootlineStatus = response.data.status
          if (this.rootlineStatus === 'started') {
            this.rootlineAttachError = this.$gettext('Your Rootline\'s connection request is being processed, please contact our support team if it takes more than 2 business days.')
          } else if (this.rootlineStatus === 'failed') {
            this.rootlineAttachError = this.$gettext('Error verifying Rootline account.')
          } else if (this.rootlineStatus === 'disconnected') {
            this.rootlineAttachError = this.$gettext('Your Rootline account was disconnected.')
          } else { // verified
            this.amountLTEZero = this.invoice.amount_to_be_paid < 1 // min amount is 1 euro for rootline
            
            if (this.currentCompany.companysettings.attach_rootline_payment && !this.currencyNotEuro && !this.amountLTEZero) {
              this.sendDetails.attach_rootline_payment = true
              this.attachRootlinePayment()
            } else if (this.currencyNotEuro || this.amountLTEZero) {
              this.rootlineDisabled = true
            }
          }
        }).catch((error) => {
          if (error.status === 404) {
            this.rootlineStatus = 'not-found'
            this.rootlineAttachError = this.$gettext('Your Rootline profile was not found.')
          } else {
            this.rootlineStatus = 'error'
          }
        })
      }
    },
    setCustomerData (customer) {
      if (this.$route.params['emailMessageId']) {
        EmailMessageNotificationService.retrieve(this.$route.params['emailMessageId']).then((response) => {
          this.$set(this.sendDetails, 'recipients', response.data.recipients)
          this.$set(this.sendDetails, 'message', response.data.message)

          if (this.recurrentResponse && this.recurrentResponse.recurrent) {
            this.$set(this.sendDetails, 'invoiceId', this.recurrentResponse.number)
          }
          this.$set(this.sendDetails, 'subject', response.data.subject)
          this.$set(this.sendDetails, 'attach_ubl', response.data.attach_ubl)
          this.$set(this.sendDetails, 'attach_costs', response.data.attach_costs)
          this.$set(this.sendDetails, 'attach_hours', response.data.attach_hours)
          this.$set(this.sendDetails, 'documents', response.data.documents)
        })
      } else {
        if (this.recurrentResponse && this.recurrentResponse.recurrent) {
          this.$set(this.sendDetails, 'invoiceId', this.recurrentResponse.number)
          this.$set(this.sendDetails, 'recipients', this.recurrentResponse.emails)
          if (this.recurrentResponse.invoice_message) {
            this.$set(this.sendDetails, 'message', this.recurrentResponse.invoice_message)
          }
        } else {
          this.sendDetails.recipients = customer.email
        }
      }

      if (this.storeSendDetails) {
        // recover previous stored sendDetails
        this.$set(this.sendDetails, 'recipients', this.storeSendDetails.recipients)
        this.$set(this.sendDetails, 'subject', this.storeSendDetails.subject)
        this.$set(this.sendDetails, 'message', this.storeSendDetails.message)
        this.$set(this.sendDetails, 'attach_mollie_payment', this.storeSendDetails.attach_mollie_payment)
        this.$set(this.sendDetails, 'attach_ubl', this.storeSendDetails.attach_ubl)
        this.$set(this.sendDetails, 'attach_hours', this.storeSendDetails.attach_hours)
        this.$set(this.sendDetails, 'attach_costs', this.storeSendDetails.attach_costs)
      }

      this.customerLoaded = true
    },
    updateMollieOnCompanySettings() {
      CompanySettingService.partialUpdate(this.currentCompany.companysettings.id, {
        'attach_mollie_payment': this.sendDetails.attach_mollie_payment
      }).then((response) => {
        // update stored company settings
        this.setCompanySettings(response.data)
      })
    },
    updateRootlineOnCompanySettings() {
      CompanySettingService.partialUpdate(this.currentCompany.companysettings.id, {
        'attach_rootline_payment': this.sendDetails.attach_rootline_payment
      }).then((response) => {
        // update stored company settings
        this.setCompanySettings(response.data)
      })
    },
    adjustAndSend (ev) {
      if (ev && ev.key && ev.target.nodeName !== 'TEXTAREA') {
        ev.preventDefault()
        return
      }
      if (!ev || (ev && !ev.key)) { // avoid submitting on key events
        this.$set(this.sendDetails, 'recipients', this.recipientsFullString)

        if (this.selectedDocuments.length > 0) {
          this.$set(this.sendDetails, 'documents', this.selectedDocuments.map(doc => doc.id).join(','))
        }

        if (!this.sendDetails.recipients || this.sendDetails.recipients === '') {
          this.$store.commit('setAlert', {
            title: this.$gettext("Recipients is empty"),
            text: this.$gettext("You need input at least one valid email address."),
            variant:"warning",
            duration: 4
          })
        } else {
          if (!this.isQuotation) {
            this.showAdjustModal = true
            this.markedAsSent = false
          } else {
            this._sendQuotation();
          }
        }
      }
    },
    sendInvoice (params) {
      this.showAdjustModal = false
      if (this.sendDetails.attach_mollie_payment && this.mollieStatus !== 'ok') {
        this.showMollieError = true
      } else if (this.sendDetails.attach_rootline_payment && this.rootlineStatus !== 'verified') {
        this.showRootlineError = true
      } else {
        this._sendInvoice(params.new_number, params.old_number)
      }
    },
    onCancelRootlineModal() {
      this.showRootlineModal = false
    },
    _sendQuotation () {
      if (this.formIsValid()) {
        this.isSending = true
        const prepared_details = Object.assign({}, this.sendDetails)
        
        prepared_details.id = prepared_details.invoiceId
        delete prepared_details.invoiceId
        delete prepared_details.attach_costs
        delete prepared_details.attach_hours

        QuotationService.send(prepared_details).then(() => {
          window.mixpanel.track("Quotations: send",{
            'recipient': this.sendDetails.recipients,
            'quotation number': this.sendDetails.subject.split(' ')[0],
            'has_attached_documents': (this.sendDetails.documents) ? this.sendDetails.documents.length : '',
            'has_attached_costs': this.sendDetails.attach_costs,
            'has_attached_hours': this.sendDetails.attach_hours,
            'has_attached_ubl': this.sendDetails.attach_ubl
          })
          this.isSending = false
          this.$store.commit('setAlert', {
            title: this.$gettext('Quotation sent'),
            text: this.$gettext('Quotation sent successfully'),
            variant: 'primary',
            duration: 4
          })
          this.$emit('invoice-updated')
          this.$router.push({ name: 'QuotationView', params: { quotationId: this.invoice.id } })
          delete this.sendDetails.errors
        }).catch((error) => {
          this.isSending = false
          if (error && error.status === 429) {
            this.$store.commit('setAlert', {
              title: this.$gettext("Quotation not sent"),
              text: this.$gettext("You need to wait before sending more quotations."),
              variant:"warning",
              duration: 4
            });
            if (!this.currentCompany.is_premium) {
              this.$store.commit('togglePremiumModal', 'web_email_throttle')
            }
          } else if (error && error.status === 500) {
            this.sendDetails.error = this.$gettext('There were some problems sending the quotation. Please try again later.')
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.error,
              variant: "danger",
              duration: 4
            })
          } else {
            this.sendDetails.errors = error.data
          }
          if (this.sendDetails.errors && this.sendDetails.errors.recipients) {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.errors.recipients[0],
              variant: "danger",
              duration: 4
            })
          }
          console.error('Sending quotations error', { errors: error, details: this.sendDetails })
        })
      }
        
    },
    _sendInvoice (new_number, old_number) {
      if (new_number && old_number) {
        const regenerated_old_name = old_number + ' ' + this.$gettext('from') + ' ' + this.currentCompany.name
        if (!this.currentCompany.is_premium || this.sendDetails.subject === regenerated_old_name) {
          this.$set(this.sendDetails, 'subject', new_number + ' ' + this.$gettext('from') + ' ' + this.currentCompany.name)
        }
        this.$set(this.invoice, 'number', new_number)
      }
      if (this.formIsValid()) {
        this.isSending = true
        if(!this.currentCompany.is_premium && this.sendDetails.attach_hours) {
          this.$set(this.sendDetails, 'attach_costs', false)
          this.sendDetails.error = this.$gettext("You can't attach hours if you are not a premium member.")
        }
        // Set to false if null or undefined
        if (!this.sendDetails.attach_mollie_payment) this.sendDetails.attach_mollie_payment = false
        if (!this.sendDetails.attach_rootline_payment) this.sendDetails.attach_rootline_payment = false
        InvoiceService.send(this.sendDetails).then(() => {
          window.mixpanel.track("Invoices: send",{
            'recipient': this.sendDetails.recipients,
            'invoice number': this.sendDetails.subject.split(' ')[0],
            'has_attached_documents': (this.sendDetails.documents) ? this.sendDetails.documents.length : '',
            'has_attached_costs': this.sendDetails.attach_costs,
            'has_attached_hours': this.sendDetails.attach_hours,
            'has_attached_ubl': this.sendDetails.attach_ubl
          })
          this.isSending = false
          this.$store.commit('setAlert', {
            title: this.$gettext('Invoice sent'),
            text: this.$gettext('Invoice sent successfully'),
            variant: 'primary',
            duration: 4
          })

          this.$emit('invoice-updated')
          
          this.updateRootlineOnCompanySettings()
          this.updateMollieOnCompanySettings()

          this.$router.push({ name: 'InvoiceView', params: { invoiceId: this.sendDetails.invoiceId } })

          delete this.sendDetails.errors
        }).catch((error) => {
          this.isSending = false
          if (error && error.status === 429) {
              this.$store.commit('setAlert', {
                  title: this.$gettext("Invoice not sent"),
                  text: this.$gettext("You need to wait before sending more invoices."),
                  variant:"warning",
                  duration: 4
              });
              if (!this.currentCompany.is_premium) {
                this.$store.commit('togglePremiumModal', 'web_email_throttle')
              }
          } else if (error && error.status === 500) {
            this.sendDetails.error = this.$gettext('There were some problems sending the invoice. Please try again later.')
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.error,
              variant: "danger",
              duration: 4
            })
          } else {
            this.sendDetails.errors = error.data
          }
          if (this.sendDetails.errors && this.sendDetails.errors.recipients) {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.errors.recipients[0],
              variant: "danger",
              duration: 4
            })
          }
          console.error('Sending invoices error', { errors: error, details: this.sendDetails })
        })
      }
    },
    formIsValid () {
      return !(!this.sendDetails.recipients) && !(!this.sendDetails.subject)
    },
    documentSelected (item) {
      this.showDocumentSelector = false
      this.selectedDocuments.push(item)
    },
    removeDocumentFromList(data) {
      switch (data.item.key) {
        case 'ublfile':
          this.$set(this.sendDetails, 'attach_ubl', false)
          break;
        case 'costsreport':
          this.$set(this.sendDetails, 'attach_costs', false)
          break;
        case 'hoursreport':
          this.$set(this.sendDetails, 'attach_hours', false)
          break;
        default:
          if (this.isQuotation) {
            this.selectedDocuments = this.selectedDocuments.filter((item) => item.id != data.item.id);
          } else {
            this.selectedDocuments = this.selectedDocuments.filter((item) => item.id != data.item.id);
            this.$refs.document_selector.items.splice(0, 0, data.item);
          }
          break;
      }
    },
    displayDocumentSelector () {
      if (this.currentCompany.is_premium || this.isQuotation) {
        this.showFileUploader = false
        this.showDocumentSelector = true
      } else {
        this.$store.commit('togglePremiumModal', 'web_documents_addtoinvoice')
      }
    },
    displayFileUploader () {
      this.showFileUploader = true
      this.showDocumentSelector = false
    },
    attachMolliePayment () {
      if (this.sendDetails.attach_mollie_payment) {
        this.showMollieModal = false
        if (this.mollieStatus !== 'ok') {
          this.$set(this.sendDetails, 'attach_mollie_payment', false)
          this.showMollieError = true;
          switch (this.mollieStatus) {
            case 'no-profile':
              this.showMollieModal = 'no-profile'
              break;
            case 'missing-data':
              this.showMollieModal = 'missing-data'
              break;
            case 'not-premium':
              this.showMollieModal = false
              break;
          }
          return false
        }
      }
    },
    attachRootlinePayment() {
      if (this.sendDetails.attach_rootline_payment) {
        if (this.rootlineStatus !== 'verified') {
          this.$set(this.sendDetails, 'attach_rootline_payment', false)
          this.showRootlineModal = true
          this.showRootlineError = true
        }
      }
    },
    detachMolliePayment () {
      this.$set(this.sendDetails, 'attach_mollie_payment', false)
    },
    detachRootlinePayment() {
      this.$set(this.sendDetails, 'attach_rootline_payment', false)
    },
    updateCCSave () {
      CompanySettingService.partialUpdate(this.currentCompany.companysettings.id, {
        'include_sender_in_cc': this.updateCC
      }).then((response) => {
        // update stored company settings
        this.setCompanySettings(response.data)
      })
    },
    /**
     * Alias for utils/niceBytes
     */
    niceBytes (size, base, unit = false) {
      return niceBytes(size, base, unit)
    },
    downloadHoursReport () {
      const api_url = window.Urls['v4:timeentry_export_hours_pdf']()
      return api_url + '?invoice=' + this.invoice.id + '&rounding_precision=' + this.currentCompany.hours_rounding_precision + '&attached_to_invoice=True&company_profile=' + window.company_id;
    }
  }
};
</script>

<style lang="scss" scoped>

</style>

<style lang="scss">
.invoice-send-wrap {
  .invoice-wrap-bg {
    padding: 0 1rem;
    @media (min-width: '992px') {
      min-height: 100%;
      .invoice-wrap {
        min-height: 100vh;
        max-width: 1200px;
        margin: 0 auto;
        padding: 2rem 0;
      }
    }
  }
}

.invoice-send-form {
  padding-bottom: 2rem;

  display: flex;
  flex-direction: column;
  gap: 2rem;

  .form-group {
    margin-bottom: 0;
  }
  
  .attachments-table-wrap {
    border-radius: $radiusLg;
    box-shadow: $filtersShadow;
    overflow: hidden;
    overflow-x: auto;

    margin-bottom: 1rem;
    .attachments-table {
      background: #f4f6f5; /* same as angular send table */
      thead {
        background: #fff;
        text-transform: none;
        color: $colorTextBase;
      }
      td:nth-child(1){ width: 40%; }
      td:nth-child(2){ width: 15%; }
      td:nth-child(3){ width: 40%; }
      td:nth-child(4){ width: 5%; }
    }
  }
  .document-selector-table-container {
    border-radius: $radiusLg;
    box-shadow: $filtersShadow;
    overflow: hidden;
    overflow-x: auto;
    .document-table {
      border-radius: $radiusLg;
    }
  }

}
</style>